// src/features/imports/components/DocumentSourcesPanel.jsx
//
// PDF-ABSTRACTION-PROVENANCE-DEPTH: Layer 2 — Document contribution summary.
//
// Collapsible "Document sources" panel rendered above the diff table in the
// PDF abstraction review step. One row per uploaded PDF file, showing:
//   - filename
//   - role badge ("primary", "amendment", "payment schedule", "supporting", "not used")
//   - count of fields contributed from that document
//   - one-sentence note from the LLM
//
// Documents that contributed zero fields show "Not used — no new values found."
//
// Props:
//   documentRoles  Array<{
//     filename:          string,
//     role:              string,
//     contributedFields: string[],
//     notes:             string | null,
//   }>

import React, { useState } from "react";
import { T } from "../../../app/tokens.js";

// ─── Role badge ───────────────────────────────────────────────────────────────

const ROLE_STYLE = {
  primary:           { bg: T.utilGreen,   fg: T.utilGreenText   },
  amendment:         { bg: T.warnAmberBg, fg: T.amberDark       },
  "payment schedule": { bg: T.blueTint ?? T.bg, fg: T.blue      },
  supporting:        { bg: T.border,      fg: T.mid             },
  "not used":        { bg: T.ragRedBg,    fg: T.ragRed          },
};

function RoleBadge({ role }) {
  const key = (role ?? "").toLowerCase();
  const s   = ROLE_STYLE[key] ?? { bg: T.border, fg: T.mid };
  return (
    <span
      data-testid={`doc-role-badge-${role}`}
      style={{
        fontSize: T.fsXxs, fontWeight: T.fwBold, whiteSpace: "nowrap",
        padding: "1px 7px", borderRadius: 4,
        background: s.bg, color: s.fg, flexShrink: 0,
      }}
    >
      {role ?? "unknown"}
    </span>
  );
}

// ─── DocumentSourcesPanel ─────────────────────────────────────────────────────

export function DocumentSourcesPanel({ documentRoles = [] }) {
  const [open, setOpen] = useState(false);

  if (!documentRoles || documentRoles.length === 0) return null;

  return (
    <div
      data-testid="document-sources-panel"
      style={{
        marginBottom: 12,
        border: `1px solid ${T.border}`,
        borderRadius: 6,
        background: T.bg,
        overflow: "hidden",
      }}
    >
      {/* ── Toggle ── */}
      <button
        type="button"
        data-testid="document-sources-toggle"
        onClick={() => setOpen((p) => !p)}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          width: "100%", padding: "8px 12px",
          background: "none", border: "none",
          borderBottom: open ? `1px solid ${T.border}` : "none",
          cursor: "pointer", textAlign: "left",
          fontSize: T.fsXxs, fontWeight: T.fwSemibold, color: T.dark,
        }}
      >
        <span style={{ fontSize: "9px", color: T.light }}>{open ? "▾" : "▸"}</span>
        Document sources
        <span style={{ marginLeft: "auto", fontWeight: T.fwNormal, color: T.mid }}>
          {documentRoles.length} {documentRoles.length === 1 ? "file" : "files"}
        </span>
      </button>

      {/* ── Rows ── */}
      {open && (
        <div data-testid="document-sources-body">
          {documentRoles.map((doc, idx) => {
            const fieldCount = Array.isArray(doc.contributedFields)
              ? doc.contributedFields.length
              : 0;
            const isNotUsed =
              fieldCount === 0 || (doc.role ?? "").toLowerCase() === "not used";

            return (
              <div
                key={idx}
                data-testid={`document-source-row-${idx}`}
                style={{
                  padding: "8px 12px",
                  borderBottom:
                    idx < documentRoles.length - 1 ? `1px solid ${T.border}` : "none",
                  display: "flex", flexDirection: "column", gap: 3,
                }}
              >
                {/* Filename + role + field count */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span
                    data-testid={`doc-filename-${idx}`}
                    style={{
                      fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark,
                      flex: 1, minWidth: 0,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                  >
                    📄 {doc.filename}
                  </span>
                  <RoleBadge role={doc.role} />
                  <span
                    data-testid={`doc-field-count-${idx}`}
                    style={{ fontSize: T.fsXxs, color: T.mid, whiteSpace: "nowrap" }}
                  >
                    {isNotUsed
                      ? "Not used - no new values found"
                      : `${fieldCount} ${fieldCount === 1 ? "field" : "fields"} contributed`}
                  </span>
                </div>

                {/* Notes */}
                {doc.notes && (
                  <p
                    data-testid={`doc-notes-${idx}`}
                    style={{
                      margin: 0, fontSize: T.fsXxs, color: T.mid,
                      fontStyle: "italic", lineHeight: 1.4,
                    }}
                  >
                    {doc.notes}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
